import React, { useEffect } from 'react';

// Actions
import { getOmniRiskProfiles } from '@/redux/modules/omni-risk-profiles';
import { showModal } from '@/redux/modules/omni-risk-profile-modal';

// Components
import LoadingScreen from '@/views/components/common/LoadingScreen';
import OmniRiskProfileModal from '@/views/components/modals/risk-profile/OmniRiskProfileModal';
import OmniRiskProfilesTable from '@/views/components/tables/OmniRiskProfilesTable';
import { withErrorBoundary } from '@/views/components/errors';
import { ContactDetailsOmniRiskProfileHeader } from './components/ContactDetailsOmniRiskProfileHeader';

// Libraries
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { RouteComponentProps, withRouter } from 'react-router';

// Types
import Locale from '@/types/locale';
import { CurrentContact } from '@/redux/modules/types/contacts';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import localStorageUtils from '@/utils/local-storage';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

// Hooks
import { useRiskProfileSearch } from './hooks/useRiskProfileSearch';

type StateProps = {
  currentContact?: CurrentContact;
  loading: boolean;
  locale: Locale;
  omniRiskProfiles: FormattedFullOmniRiskProfile[];
  user?: Nullable<User>;
};

type DispatchProps = {
  getOmniRiskProfiles: () => Promise<void>;
  showModal: () => void;
};

type Props = StateProps &
  DispatchProps &
  RouteComponentProps & {
    onCreateComparisonFromProfile: (profile: FormattedFullOmniRiskProfile) => void;
    onDeleteRiskProfile: (profile: FormattedFullOmniRiskProfile) => void;
  };

const ContactDetailsOmniRiskProfiles = (props: Props) => {
  const { currentContact, loading, locale, omniRiskProfiles, user, getOmniRiskProfiles, onCreateComparisonFromProfile, onDeleteRiskProfile, showModal } = props;

  const { query, searching, filteredProfiles, handleSearch } = useRiskProfileSearch({
    profiles: omniRiskProfiles,
    locale,
  });

  const persistenceSource = user ? localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) : null;
  const isProspect = !!user && persistenceSource == null;
  const isClientWithPermission = !!user && persistenceSource !== null && userHasPermission(user, PERMISSIONS.OMNI_RISK_PROFILE_EXISTING_CLIENT_CREATE);
  const allowsQuestionnaireActions = isProspect || isClientWithPermission;

  useEffect(() => {
    getOmniRiskProfiles();
  }, [getOmniRiskProfiles]);

  const onCreateQuestionnaireHandler = () => {
    showModal();
  };

  return (
    <>
      <div className="contact-details-risk-profiles table-wrapper table-container">
        <ContactDetailsOmniRiskProfileHeader
          firstName={currentContact?.contact?.fname}
          omniRiskProfiles={omniRiskProfiles}
          query={query}
          searching={searching}
          onCreateQuestionnaire={onCreateQuestionnaireHandler}
          onSearch={handleSearch}
          showCreateQuestionnaireButton={allowsQuestionnaireActions}
        />

        <div className="contact-details-risk-profile-body">
          {loading ? (
            <LoadingScreen titleToLocalize="Contacts.ContactTabs.RiskProfiles.LoadingRiskProfiles" />
          ) : (
            <OmniRiskProfilesTable
              data={filteredProfiles}
              onCreateComparisonFromProfile={onCreateComparisonFromProfile}
              onCreateQuestionnaire={onCreateQuestionnaireHandler}
              onDelete={onDeleteRiskProfile}
              allowsQuestionnaireActions={allowsQuestionnaireActions}
            />
          )}
        </div>
      </div>

      {currentContact && <OmniRiskProfileModal />}
    </>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const {
    intl: { locale },
    omniRiskProfiles: { loading, riskProfiles },
    contacts: { current },
  } = state;

  return {
    currentContact: current,
    loading,
    locale,
    omniRiskProfiles: riskProfiles || [],
    user: state.auth.user,
  };
};

export default withRouter(
  withErrorBoundary(
    connect(mapStateToProps, {
      getOmniRiskProfiles,
      showModal,
    })(ContactDetailsOmniRiskProfiles),
  ),
);
